<template>
  <section class="service-commitment">
    <div class="content-wrapper">
      <div class="section-header">
        <h2 class="section-title">
          Service Commitment
          <div class="title-line-good"></div>
        </h2>
      </div>

      <div class="commitment-grid">
        <!-- 第一行 -->
        <div class="row-anim">
          <div
              v-for="(item, index) in firstRow"
              :key="index"
              class="commitment-card"
              @mouseenter="handleCardHover($event)"
              @mouseleave="handleCardLeave($event)"
          >
            <div class="card-border"></div>
            <div class="card-content">
              <h3 class="card-title">{{ item.title }}</h3>
              <div class="highlight-data">
                <animated-number
                    :value="item.value"
                    :duration="1600"
                    class="number"
                />
              </div>
              <p class="card-desc">{{ item.desc }}</p>
            </div>
          </div>
        </div>

        <!-- 第二行 -->
        <div class="row-anim">
          <div
              v-for="(item, index) in secondRow"
              :key="index"
              class="commitment-card"
              @mouseenter="handleCardHover($event)"
              @mouseleave="handleCardLeave($event)"
          >
            <div class="card-border"></div>
            <div class="card-content">
              <h3 class="card-title">{{ item.title }}</h3>
              <div class="highlight-data">
                <animated-number
                    :value="item.value"
                    :duration="1600"
                    class="number"
                />
              </div>
              <p class="card-desc">{{ item.desc }}</p>
            </div>
          </div>
        </div>
      </div>
      <div class="action-buttons">
        <button
            v-for="(btn, index) in buttons"
            :key="index"
            class="nav-button"
            @mouseenter="handleButtonHover(index)"
            @mouseleave="handleButtonLeave(index)"
            @click="navigateTo(btn.path)"
        >
          <span class="button-text">{{ btn.text }}</span>
          <div class="button-underline"></div>
          <div class="hover-glow"></div>
        </button>
      </div>
    </div>
  </section>
</template>

<script>
import {gsap} from 'gsap'
import {ScrollTrigger} from 'gsap/ScrollTrigger'
import {CheckBadgeIcon, ClockIcon, CpuChipIcon, ShieldCheckIcon} from '@heroicons/vue/24/outline'

gsap.registerPlugin(ScrollTrigger)

export default {
  name: 'AdvantageView',
  computed: {
    firstRow() {
      return this.commitments.slice(0, 2)
    },
    secondRow() {
      return this.commitments.slice(2, 4)
    }
  },
  data() {
    return {
      commitments: [
        {
          icon: CheckBadgeIcon,
          title: 'Free sample verification',
          value: 100,
          unit: '%',
          desc: 'Zero risk verification, place an order after satisfaction',
        },
        {
          icon: ClockIcon,
          title: 'Regular shipment guarantee',
          value: 87,
          unit: '%',
          desc: 'Overtime enjoy 8% deduction for each order',
        },
        {
          icon: CpuChipIcon,
          title: 'Custom development',
          value: 7,
          unit: '天',
          desc: 'Support development based on drawings/samples',
        },
        {
          icon: ShieldCheckIcon,
          title: 'Material price fluctuation guarantee',
          value: 8,
          unit: '%',
          desc: 'Exceeding 8% increase or decrease triggers compensation',
        }
      ],
      buttons: [
        { text: 'About', path: '/about' },
        { text: 'Product', path: '/products' },
        { text: 'Contact', path: '/contact' }
      ]
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.initScrollAnimations()
    })
  },
  methods: {
    initScrollAnimations() {
      // 标题线条动画
      gsap.from('.title-line', {
        scrollTrigger: {
          trigger: '.section-header',
          start: 'top center',
          toggleActions: 'play none none reverse'
        },
        scaleX: 0,
        duration: 1.5,
        ease: 'power4.out'
      })

      // 第一行卡片动画
      const firstCards = gsap.utils.toArray('.row-anim:first-child .commitment-card')
      firstCards.forEach((card, i) => {
        // 强制设置初始可见性
        card.style.transform = 'translateY(0)'
        card.style.opacity = '1'

        gsap.from(card, {
          scrollTrigger: {
            trigger: card,
            start: 'top 80%',
            toggleActions: 'play none none reverse'
          },
          y: 60,
          opacity: 0,
          duration: 0.8,
          delay: i * 0.2,
          ease: 'circ.out',
          // 确保离开视口时恢复初始状态
          onReverseComplete: () => {
            card.style.transform = 'translateY(0)'
            card.style.opacity = '1'
          }
        })
      })

      // 第二行卡片动画
      const secondCards = gsap.utils.toArray('.row-anim:last-child .commitment-card')
      secondCards.forEach((card, i) => {
        card.style.transform = 'translateY(0)'
        card.style.opacity = '1'

        gsap.from(card, {
          scrollTrigger: {
            trigger: card,
            start: 'top 90%',
            toggleActions: 'play none none reverse'
          },
          y: 60,
          opacity: 0,
          duration: 0.8,
          delay: i * 0.2 + 0.4,
          ease: 'circ.out',
          onReverseComplete: () => {
            card.style.transform = 'translateY(0)'
            card.style.opacity = '1'
          }
        })
      })

      // 边框发光动画和描述文字显示
      gsap.utils.toArray('.commitment-card').forEach(card => {
        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: card,
            start: 'top 80%',
            toggleActions: 'play none none reverse'
          }
        })

        // 发光动画
        tl.to(card.querySelector('.card-border'), {
          duration: 1.2,
          ease: 'power2.out',
          '--border-glow': '0 0 10px 2px rgba(197,164,126, 0.8)',
          cssVars: true
        })

        // 描述文字显示动画
        tl.fromTo(
            card.querySelector('.card-desc'),
            { y: 20, opacity: 0 },
            { y: 0, opacity: 1, duration: 0.6 },
            0.2 // 延迟0.2秒后执行
        )
      })
    },
    handleButtonHover(index) {
      // 先重置所有按钮状态
      gsap.killTweensOf('.nav-button')
      gsap.set('.button-underline', { width: 0 })

      // 仅激活当前按钮
      const button = this.$el.querySelectorAll('.nav-button')[index]
      gsap.to(button.querySelector('.button-underline'), {
        width: '80%',
        duration: 0.3
      })
    },
    handleButtonLeave(index) {
      const button = this.$el.querySelectorAll('.nav-button')[index]
      gsap.to(button, {
        duration: 0.3,
        y: 0,
        ease: 'power2.out'
      })
      gsap.to(button.querySelector('.button-underline'), {
        width: '0%',
        duration: 0.3
      })
      gsap.to(button.querySelector('.hover-glow'), {
        opacity: 0,
        duration: 0.3
      })
    },
    handleCardHover(e) {
      const card = e.currentTarget
      gsap.to(card, {
        y: -8,
        duration: 0.4,
        ease: 'power2.out',
        overwrite: 'auto' // 添加动画覆盖模式
      })
      gsap.to(card.querySelector('.card-border'), {
        '--border-glow': '0 0 20px 3px rgba(197,164,126, 0.8)',
        duration: 0.4,
        overwrite: true
      })
      gsap.to(card, {
        boxShadow: '0 12px 30px rgba(10, 26, 47, 0.4)',
        duration: 0.4,
        zIndex: 10  // 提升悬停卡片层级
      })
    },
    handleCardLeave(e) {
      const card = e.currentTarget
      gsap.to(card, {
        y: 0,
        boxShadow: '0 4px 15px rgba(10, 26, 47, 0.2)', // 恢复初始投影
        duration: 0.4,
        ease: 'power2.out',
        zIndex: 1  // 恢复默认层级
      })
      gsap.to(card.querySelector('.card-border'), {
        '--border-glow': '0 0 10px 2px rgba(197,164,126, 0.3)',
        duration: 0.4
      })
    },
    navigateTo(path, params = {}) {
      this.$navigate.path(path, {
        query: params.query,
        params: params.params
      });
    }
  }
}
</script>

<style lang="scss" scoped>
.service-commitment {
  padding: 8rem 0;
  background: transparent; /* 原背景色 #0A1A2F 移除 */
  backdrop-filter: blur(20px);
  //border: 1px solid rgba(197, 164, 126, 0.1);
  will-change: backdrop-filter, transform;

  position: relative;

  //&::after {
  //    content: '';
  //    position: absolute;
  //    bottom: -50px;
  //    left: 0;
  //    width: 100%;
  //    height: 100px;
  //    background: linear-gradient(to bottom,
  //        rgba(18, 36, 61, 1) 0%,
  //        rgba(18, 36, 61, 0) 100%
  //    );
  //    z-index: 2;
  //}

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    //background:
    //  linear-gradient(45deg,
    //    rgba(197, 164, 126, 0.03) 0%,
    //    rgba(10, 26, 47, 0.9) 60%
    //  ),
    //  repeating-linear-gradient(
    //    -45deg,
    //    rgba(197, 164, 126, 0.02),
    //    rgba(197, 164, 126, 0.02) 1px,
    //    transparent 1px,
    //    transparent 4px
    //  );
  }
}

.section-header {
  text-align: center;
  margin-bottom: 6rem;
}

.section-title {
  font-size: 2.8rem;
  color: #C5A47E;
  position: relative;
  display: inline-block;
  padding-bottom: 1rem;
}

.title-line {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 2px;
  background: linear-gradient(90deg, transparent, #C5A47E 50%, transparent);
  transform-origin: left center;
}

.commitment-grid {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
}

.row-anim {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;
  margin-bottom: 2rem;

  &:last-child {
    margin-bottom: 0;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
}

.commitment-card {
  position: relative;
  padding: 2rem;
  border-radius: 12px;
  overflow: hidden;

  transition: box-shadow 0.4s ease;
  will-change: transform, box-shadow;
  transform-style: preserve-3d;
  backface-visibility: hidden;

  // 添加初始投影
  box-shadow: 0 4px 15px rgba(10, 26, 47, 0.2);
}

.card-border {
  position: absolute;
  inset: 0;
  border: 1px solid rgba(197, 164, 126, 0.3);
  border-radius: 12px;

  --border-glow: 0 0 10px 2px rgba(197, 164, 126, 0.3);
  box-shadow: var(--border-glow);
  transition: box-shadow 0.4s ease;
}

.card-content {
  position: relative;
  z-index: 1;
}

.card-title {
  font-size: 1.5rem;
  color: #C5A47E;
  margin-bottom: 1.5rem;
}

.number {
  font-size: 2.4rem;
  color: #C5A47E;
  font-weight: 300;
  display: block;
  margin-bottom: 1.5rem;
}

.card-desc {
  font-size: 1rem;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.8);
  // 初始可见
  opacity: 0.5;
  transform: translateY(0);
}

@media (max-width: 768px) {
  .section-title {
    font-size: 2.2rem;
  }

  .commitment-card {
    padding: 1.5rem;
    box-shadow: none !important;
    /* 移动端需要强制重置transform */
    transform: none !important;
  }

  .number {
    font-size: 2rem;
  }
}

/* 按钮容器样式 */
.action-buttons {
  display: flex;
  justify-content: center;
  gap: 2rem;
  margin-top: 6rem;

  @media (max-width: 768px) {
    gap: 1rem;
    width: 100%;
    padding: 0 16px;
    box-sizing: border-box;

    /* 移动端完美居中方案 */
    display: flex;
    flex-wrap: nowrap;
    justify-content: center;
  }

  @media (max-width: 480px) {
    gap: 0.8rem;
    padding: 0 12px;
  }
}

/* 按钮本体样式 */
.nav-button {
  position: relative;
  padding: 0.8rem 2rem;
  min-width: 140px;
  background: transparent;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;

  /* 修复动画冲突的关键设置 */
  pointer-events: auto;
  isolation: isolate;

  .button-text {
    font-size: 1.1rem;
    letter-spacing: 0.1em;
    color: #C5A47E;
    position: relative;
    z-index: 2;
    transition: color 0.3s ease;
  }

  .button-underline {
    position: absolute;
    bottom: 0;
    left: 50%;
    width: 0;
    height: 1px;
    background: #C5A47E;
    transform: translateX(-50%);
    transition: width 0.3s ease;
  }

  /* 移动端适配 */
  @media (max-width: 768px) {
    min-width: 110px;
    padding: 0.7rem 1.5rem;
    flex-shrink: 0; /* 防止按钮挤压 */

    .button-text {
      font-size: 0.95rem;
    }
  }

  @media (max-width: 480px) {
    min-width: 90px;
    padding: 0.6rem 1rem;
    .button-text {
      font-size: 0.88rem;
    }
  }

  /* 悬停状态 */
  &:hover {
    .button-underline {
      width: 80%; /* 控制下划线展开长度 */
    }

    .button-text {
      color: lighten(#C5A47E, 10%);
    }
  }

  /* 边框动画 */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border: 1px solid rgba(197, 164, 126, 0.3);
    border-radius: 30px;
    transition: all 0.3s ease;
    z-index: -1;
  }

  &:hover::before {
    border-color: rgba(197, 164, 126, 0.6);
    box-shadow: 0 0 15px rgba(197, 164, 126, 0.2);
  }
}
</style>
